import React, { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import drilldown from "highcharts/modules/drilldown";
drilldown(Highcharts);

const PAGE_SIZE = 5;

function aggregateByUser(tickets) {
  const counts = {};
  tickets.forEach((t) => {
    const user = t.user || t.admin || "Unknown";
    counts[user] = (counts[user] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([user, count]) => ({ user, count }))
    .sort((a, b) => b.count - a.count);
}

function aggregateByCategory(tickets) {
  const catCounts = {};
  const subcatCounts = {};
  tickets.forEach((t) => {
    const cat = t.category || "Unknown";
    const subcat = t.subCategory || "Other";
    catCounts[cat] = (catCounts[cat] || 0) + 1;
    if (!subcatCounts[cat]) subcatCounts[cat] = {};
    subcatCounts[cat][subcat] = (subcatCounts[cat][subcat] || 0) + 1;
  });
  return { catCounts, subcatCounts };
}

const SupportReporting = ({ totalCount }) => {
  // Date Range State
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29); // last 30 days including today
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  });

  // Filter tickets by date range
  const filteredTickets = useMemo(() => {
    if (!totalCount) return [];
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999); // include the whole end day
    const filtered = totalCount.filter((t) => {
      if (!t.createdOn) return true; // include if missing createdAt
      const created = new Date(t.createdOn);
      if (isNaN(created.getTime())) return true; // include if invalid date
      return created >= startDate && created <= endDate;
    });
    // Debug log
    console.log('Filtered Tickets:', filtered);
    return filtered;
  }, [totalCount, dateRange]);

  // Top Users Table
  const [page, setPage] = useState(1);
  const users = useMemo(() => aggregateByUser(filteredTickets), [filteredTickets]);
  const pagedUsers = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Pie Chart
  const { catCounts, subcatCounts } = useMemo(() => aggregateByCategory(filteredTickets), [filteredTickets]);
  const [drilldownCat, setDrilldownCat] = useState(null);

  const pieOptions = useMemo(() => {
    if (!drilldownCat) {
      return {
        chart: { type: "pie" },
        title: { text: "Tickets by Category" },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
            events: {
              click: function (e) {
                setDrilldownCat(e.point.name);
              },
            },
          },
        },
        series: [
          {
            name: "Tickets",
            colorByPoint: true,
            data: Object.entries(catCounts).map(([name, y]) => ({ name, y })),
          },
        ],
      };
    } else {
      // Drilldown to subcategories
      const subcats = subcatCounts[drilldownCat] || {};
      return {
        chart: { type: "pie" },
        title: { text: `Subcategories in ${drilldownCat}` },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
            events: {
              click: function () {
                setDrilldownCat(null);
              },
            },
          },
        },
        series: [
          {
            name: "Tickets",
            colorByPoint: true,
            data: Object.entries(subcats).map(([name, y]) => ({ name, y })),
          },
        ],
      };
    }
  }, [catCounts, subcatCounts, drilldownCat]);

  // Table columns
  const columns = [
    { name: "User", selector: (row) => row.user, sortable: true },
    { name: "Ticket Count", selector: (row) => row.count, sortable: true },
  ];

  return (
    <div style={{ margin: "24px 0" }}>
      
      {filteredTickets.length === 0 ? (
        <div style={{ color: 'red', marginBottom: 16 }}>No tickets found for the selected date range.</div>
      ) : null}
      <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 350 }}>
          <h4>Top Users by Ticket Count 
        <input
        style={{margin:"10px"}}
          type="date"
          value={dateRange.start}
          max={dateRange.end}
          onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
        />
       
        <input
        style={{margin:"10px"}}
          type="date"
          value={dateRange.end}
          min={dateRange.start}
          max={new Date().toISOString().slice(0, 10)}
          onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
        />
      </h4>
          <DataTable
            columns={columns}
            data={pagedUsers}
            pagination
            paginationServer
            paginationTotalRows={users.length}
            paginationPerPage={PAGE_SIZE}
            onChangePage={setPage}
            highlightOnHover
            noHeader
          />
        </div>
        <div style={{ flex: 1, minWidth: 350 }}>
          <h4>Tickets by Category</h4>
          <HighchartsReact highcharts={Highcharts} options={pieOptions} />
          {drilldownCat && (
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <button onClick={() => setDrilldownCat(null)}>Back to Categories</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportReporting; 